"use client"

import { motion } from "framer-motion"
import Image from "next/image"
import { useState } from "react"
import { Globe } from "lucide-react"
import {
  LinkedinIcon, TwitterXIcon, GithubIcon,
  InstagramIcon, YoutubeIcon, DiscordIcon,
} from "@/components/social-icons"
import { teamMembers } from "@/features/home/data"
import { staggerContainer, staggerItem, fadeUp, sectionLabel } from "@/lib/motion"

const VP = { once: true, amount: 0.05 }

function TeamCard({ member }: { member: typeof teamMembers[0] }) {
  const [isHovered, setIsHovered] = useState(false)
  const [hasImage, setHasImage] = useState(Boolean(member.image))
  const initials = member.name.split(" ").map((n) => n[0]).join("")

  return (
    <motion.div
      variants={staggerItem}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group w-[min(100%,10.5rem)] sm:w-64 lg:w-64 flex-none"
    >
      <div className="glass hairline flex h-[25rem] sm:h-[33rem] flex-col rounded-2xl p-5 sm:p-7 text-center transition-shadow duration-300 hover:glow-accent">
        <div className="relative mx-auto mb-5 h-32 w-28 shrink-0 sm:mb-6 sm:h-44 sm:w-40">
          <div
            className={`absolute inset-0 rounded-3xl transition-opacity duration-300 bg-primary/20 blur-2xl ${isHovered ? "opacity-100" : "opacity-0"}`}
          />
          <div
            className={`relative w-full h-full overflow-hidden rounded-3xl bg-secondary/70 transition-[box-shadow] duration-300 ${isHovered ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : "ring-1 ring-border/70"}`}
          >
            <div
              className="relative w-full h-full bg-gradient-to-br from-secondary to-muted flex items-center justify-center"
            >
              {hasImage ? (
                <Image
                  src={member.image}
                  alt={member.name}
                  fill
                  sizes="(min-width: 640px) 160px, 112px"
                  quality={95}
                  className={`object-cover object-[center_30%] transition-transform duration-300 ${isHovered ? "scale-105" : "scale-100"}`}
                  onError={() => setHasImage(false)}
                />
              ) : (
                <span className="font-[family-name:var(--font-heading)] text-2xl sm:text-4xl font-bold text-muted-foreground">
                  {initials}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          <h3 className="flex min-h-10 items-center justify-center font-[family-name:var(--font-heading)] text-sm sm:min-h-14 sm:text-xl font-bold text-foreground leading-tight line-clamp-2">
            {member.name}
          </h3>
          <p className="flex min-h-11 items-center justify-center text-primary font-medium text-xs sm:min-h-12 sm:text-sm line-clamp-2">{member.role}</p>
          <p className="flex min-h-20 items-start justify-center text-muted-foreground text-xs leading-relaxed sm:min-h-24 sm:text-sm line-clamp-4">{member.bio}</p>
        </div>

        {Object.values(member.social).some(Boolean) && (
          <div className="mt-5 flex shrink-0 flex-wrap items-center justify-center gap-2">
            {member.social.linkedin && (
              <a href={member.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label={`${member.name} LinkedIn`} className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors">
                <LinkedinIcon className="w-4 h-4" />
              </a>
            )}
            {member.social.twitter && (
              <a href={member.social.twitter} target="_blank" rel="noopener noreferrer" aria-label={`${member.name} Twitter / X`} className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors">
                <TwitterXIcon className="w-4 h-4" />
              </a>
            )}
            {member.social.github && (
              <a href={member.social.github} target="_blank" rel="noopener noreferrer" aria-label={`${member.name} GitHub`} className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors">
                <GithubIcon className="w-4 h-4" />
              </a>
            )}
            {member.social.instagram && (
              <a href={member.social.instagram} target="_blank" rel="noopener noreferrer" aria-label={`${member.name} Instagram`} className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors">
                <InstagramIcon className="w-4 h-4" />
              </a>
            )}
            {member.social.discord && (
              <a href={member.social.discord} target="_blank" rel="noopener noreferrer" aria-label={`${member.name} Discord`} className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors">
                <DiscordIcon className="w-4 h-4" />
              </a>
            )}
            {member.social.youtube && (
              <a href={member.social.youtube} target="_blank" rel="noopener noreferrer" aria-label={`${member.name} YouTube`} className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors">
                <YoutubeIcon className="w-4 h-4" />
              </a>
            )}
            {member.social.website && (
              <a href={member.social.website} target="_blank" rel="noopener noreferrer" aria-label={`${member.name} kişisel web sitesi`} className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors">
                <Globe className="w-4 h-4" />
              </a>
            )}
          </div>
        )}
      </div>
    </motion.div>
  )
}

export function TeamSection() {
  return (
    <section id="ekip" className="relative py-16 sm:py-24 lg:py-32" aria-label="Ekibimiz">
      <div className="brand-divider" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 max-w-6xl">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={VP}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <motion.span variants={sectionLabel} className="text-primary font-medium text-sm uppercase tracking-wider inline-block">
            Ekibimiz
          </motion.span>
          <motion.h2 variants={fadeUp} className="font-[family-name:var(--font-heading)] text-3xl sm:text-4xl md:text-5xl font-bold mt-4 mb-5 sm:mb-6 text-foreground">
            Uzman <span className="text-gradient-brand">Kadromuz</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground text-lg leading-relaxed">
            Deneyimli ve tutkulu ekibimizle, her projede en iyisini sunmak için
            çalışıyoruz.
          </motion.p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={VP}
          className="flex flex-wrap justify-center gap-4 sm:gap-6"
        >
          {teamMembers.map((member) => (
            <TeamCard key={member.name} member={member} />
          ))}
        </motion.div>
      </div>
    </section>
  )
}
